/* ==========================================================================
   Bedrock — BDR Méga-menu (bdr-mega-menu)
   Dropdown desktop, drawer mobile, overlay transparent & sticky scroll-up
   ========================================================================== */

(function () {
  'use strict';

  if (customElements.get('bdr-mega-menu')) return;

  /* Les chemins d'ajout au panier de ce theme n'emettent pas le meme signal :
     les sections bdr- passent par `bdr:cart:updated`, la barre d'achat collante
     par `bdr:cart:update` et `cart:refresh`, et les fiches produit Dawn par le
     pubsub, relaye en `bdr:cart:count` par `standard-actions-override.js`.
     Le compteur ecoute les quatre. */
  const BDR_CART_EVENTS = ['bdr:cart:updated', 'bdr:cart:count', 'bdr:cart:update', 'cart:refresh'];

  class BdrMegaMenu extends HTMLElement {
    connectedCallback() {
      this.triggers = Array.from(this.querySelectorAll('[data-bdr-mega-trigger]'));
      this.panels = Array.from(this.querySelectorAll('[data-bdr-mega-panel]'));
      this.burger = this.querySelector('[data-bdr-mega-burger]');
      this.drawer = this.querySelector('[data-bdr-mega-drawer]');

      this.overlayMode = this.dataset.overlayMode || 'none';
      this.stickyMode = this.dataset.stickyMode || 'always';
      this.solidAfter = parseInt(this.dataset.solidAfter || '80', 10);
      this.isDesignMode = Boolean(window.Shopify && window.Shopify.designMode);

      this.lastScrollY = window.scrollY;
      this.activePanel = null;

      this.onTriggerClick = this.handleTriggerClick.bind(this);
      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onScroll = this.handleScroll.bind(this);

      this.triggers.forEach((trig) => trig.addEventListener('click', this.onTriggerClick));
      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('keydown', this.onKeydown);

      // Selecteur pays du tiroir mobile : le changement de devise part au
      // `change`, comme dans la barre d'annonce. Le <noscript> du snippet prend
      // le relais quand le script ne tourne pas.
      this.localeSelect = this.querySelector('[data-bdr-drawer-locale]');
      if (this.localeSelect) {
        this.onLocaleChange = this.handleLocaleChange.bind(this);
        this.localeSelect.addEventListener('change', this.onLocaleChange);
      }

      // Compteur panier de l'en-tete. Le badge est rendu serveur ; on le
      // resynchronise ensuite sur chaque mouvement de panier.
      this.cartCounts = Array.from(this.querySelectorAll('[data-bdr-cart-count]'));
      if (this.cartCounts.length) {
        this.onCartUpdated = this.handleCartUpdated.bind(this);
        BDR_CART_EVENTS.forEach((name) => document.addEventListener(name, this.onCartUpdated));
      }

      this.updateHeaderHeight();
      this.resizeObserver = new ResizeObserver(() => this.updateHeaderHeight());
      this.resizeObserver.observe(this);

      if (this.stickyMode !== 'none' || this.overlayMode === 'transparent') {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.handleScroll();
      }
    }

    disconnectedCallback() {
      this.triggers.forEach((trig) => trig.removeEventListener('click', this.onTriggerClick));
      document.removeEventListener('click', this.onDocumentClick);
      this.removeEventListener('keydown', this.onKeydown);
      if (this.localeSelect) this.localeSelect.removeEventListener('change', this.onLocaleChange);
      if (this.onCartUpdated) {
        BDR_CART_EVENTS.forEach((name) => document.removeEventListener(name, this.onCartUpdated));
      }
      window.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    updateHeaderHeight() {
      const h = this.offsetHeight;
      if (h > 0) {
        document.documentElement.style.setProperty('--bdr-header-height', `${h}px`);
      }
    }

    handleScroll() {
      const currentScrollY = window.scrollY;
      const isPastThreshold = currentScrollY > this.solidAfter;

      // L'ancrage reste actif dans l'editeur de theme : sans cela aucun reglage
      // de « Mode de defilement » n'est observable pendant qu'on configure.
      // Seul le masquage au scroll descendant reste desactive plus bas, parce
      // qu'un en-tete qui disparait rend la selection des sections penible.
      // On memorise la position naturelle du menu avant de le detacher, sinon
      // offsetTop vaut 0 une fois en position fixe et il se recolle en boucle.
      if (this.stickyMode !== 'none' && this.overlayMode !== 'transparent') {
        if (this.pinOffset === undefined || (!this.isPinned && this.offsetTop > 0)) {
          this.pinOffset = this.offsetTop;
        }
        const shouldPin = currentScrollY > (this.pinOffset || 0);
        if (shouldPin !== this.isPinned) {
          this.isPinned = shouldPin;
          this.classList.toggle('bdr-mega-menu--pinned', shouldPin);
          this.updateHeaderHeight();
        }
      }

      if (this.isDesignMode) {
        this.lastScrollY = currentScrollY;
        return;
      }

      // Overlay transparent -> solid handling
      if (this.overlayMode === 'transparent') {
        if (isPastThreshold) {
          this.classList.add('bdr-mega-menu--solid');
          this.classList.remove('bdr-mega-menu--transparent');
        } else {
          this.classList.add('bdr-mega-menu--transparent');
          this.classList.remove('bdr-mega-menu--solid');
        }
      }

      // Sticky mode on-scroll-up
      if (this.stickyMode === 'on-scroll-up') {
        if (currentScrollY > 120 && currentScrollY > this.lastScrollY && isPastThreshold) {
          // Scrolling down
          this.classList.add('bdr-mega-menu--hidden');
          this.closeAllPanels();
        } else {
          // Scrolling up
          this.classList.remove('bdr-mega-menu--hidden');
        }
      }

      this.lastScrollY = currentScrollY;
    }

    handleTriggerClick(event) {
      const btn = event.currentTarget;
      const targetId = btn.getAttribute('aria-controls');
      const panel = this.querySelector(`#${targetId}`);

      if (!panel) return;

      const isExpanded = btn.getAttribute('aria-expanded') === 'true';

      this.closeAllPanels();

      if (!isExpanded) {
        btn.setAttribute('aria-expanded', 'true');
        panel.classList.add('is-open');
        this.activePanel = panel;
      }
    }

    closeAllPanels() {
      this.triggers.forEach((trig) => trig.setAttribute('aria-expanded', 'false'));
      this.panels.forEach((p) => p.classList.remove('is-open'));
      this.activePanel = null;
    }

    handleDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.closeAllPanels();
      }
    }

    handleLocaleChange(event) {
      const form = event.target.closest('form');
      if (form) form.submit();
    }

    /* Le detail ne porte le panier complet que sur une partie des chemins :
       `Bedrock.changeCart` et le tiroir renvoient bien `item_count`, alors que
       tout ce qui vient d'un /cart/add.js renvoie la ligne ajoutee, qui ne l'a
       pas. Dans ce cas on va lire l'etat reel du panier. */
    handleCartUpdated(event) {
      const detail = event.detail;
      if (detail && typeof detail.item_count === 'number') {
        this.setCartCount(detail.item_count);
        return;
      }
      this.syncCartCount();
    }

    /* Un meme ajout peut declencher deux de ces evenements. On numerote les
       requetes pour qu'une reponse en retard n'ecrase pas un compte plus
       recent. En cas d'echec le badge garde sa valeur precedente. */
    async syncCartCount() {
      const seq = (this.cartSyncSeq = (this.cartSyncSeq || 0) + 1);
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        if (seq !== this.cartSyncSeq) return;
        this.setCartCount(cart.item_count);
      } catch (error) {
        /* silencieux : le badge reste sur sa derniere valeur connue */
      }
    }

    setCartCount(count) {
      this.cartCounts.forEach((node) => {
        node.textContent = count;
        node.hidden = count === 0;
      });
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.closeAllPanels();
      }
    }
  }

  customElements.define('bdr-mega-menu', BdrMegaMenu);
})();
